import React, { useMemo, useState } from "react";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    LoadingProgress,
    Modal,
    Panel,
    Table,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useMutation } from "@apollo/react-hooks";
import {
    MUTATION_UPDATE_AEROPAY,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import omit from "lodash/omit";
import { useFormActions, useForm, useMeta, useNotification } from "@/hooks";
import { useStoreContext } from "context";
import AeropayCredentialForm from "./AeropayCredentialForm";
import {
    PlusIcon,
    RowActionsMenu,
    legacyColorToBadge,
    storeTableUiStyles,
} from "./shared";
import styles from "./GeneralsForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const AeropayForm = ({ app, loading: pageLoading }) => {
    const { user } = useMeta();
    const { providers } = useStoreContext();
    const [showForm, setShowForm] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [rowSelect, setRowSelect] = useState(null);
    const [credentials, setCredentials] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const notify = useNotification();

    const { inputs, setInputs } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    const [mutate] = useMutation(MUTATION_UPDATE_AEROPAY, {
        refetchQueries: [],
        onCompleted: () => {
            handleCancel();
            setIsUpdating(false);
            setIsDelete(false);
            setShowDeleteConfirm(false);
            setInputs({ ...inputs, credentials });
            notify(
                `Credential Aeropay Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully"`,
                "success",
            );
        },
        onError: () => {
            setIsUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  The Credential, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const formatCredentials = credentialsList => {
        if (!credentialsList) return [];

        return credentialsList.map(credential => ({
            sandbox: credential?.aeropay_sandbox,
            host: credential.aeropay_host,
            api_key: credential.aeropay_api_key,
            api_secret: credential.aeropay_api_secret,
            merchant_id: credential.aeropay_merchant_id,
            location_uuid: credential.aeropay_location_uuid,
            store_provider_id: credential.aeropay_store_provider_id,
        }));
    };

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...omit(
                    {
                        ...inputs,
                        id: inputs.id,
                        host: inputs.host_setting,
                        api_key: inputs.api_key_setting,
                        credentials: formatCredentials(inputs?.credentials),
                        type: "Aeropay",
                    },
                    ["api_key_setting", "host_setting"],
                ),
            },
        },
    };

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Aeropay",
        "",
        MUTATION_UPDATE_AEROPAY,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
                skip: user.loading || !user.accountId || !user.storeId,
            },
        ],
        options,
        "",
        () => true,
        () => true,
        "primary",
    );

    const rawCredentials = (inputs && inputs.credentials) || [];
    const rows = useMemo(
        () =>
            rawCredentials.map((credential, index) => ({
                ...credential,
                _index: index,
            })),
        [rawCredentials],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm}
                            label={
                                row.aeropay_store_provider_id ||
                                `credential ${row._index + 1}`
                            }
                            onDelete={() => {
                                setIsDelete(true);
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowForm(true);
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "aeropay_store_provider_id",
                header: "Store Id",
                minWidth: 180,
                renderCell: row =>
                    row.aeropay_store_provider_id ? (
                        <Badge variant="primary">
                            {row.aeropay_store_provider_id}
                        </Badge>
                    ) : (
                        ""
                    ),
            },
            {
                id: "aeropay_sandbox",
                header: "Sandbox",
                minWidth: 100,
                renderCell: row => (
                    <Badge
                        variant={legacyColorToBadge(
                            row.aeropay_sandbox ? "success" : "error",
                        )}
                    >
                        {row.aeropay_sandbox ? "Yes" : "No"}
                    </Badge>
                ),
            },
            {
                id: "aeropay_host",
                header: "Host",
                minWidth: 180,
                renderCell: row => row.aeropay_host || "",
            },
            {
                id: "aeropay_api_key",
                header: "Api key",
                minWidth: 180,
                renderCell: row => row.aeropay_api_key || "",
            },
            {
                id: "aeropay_api_secret",
                header: "Api Secret",
                minWidth: 180,
                renderCell: row => row.aeropay_api_secret || "",
            },
            {
                id: "aeropay_merchant_id",
                header: "Merchant Id",
                minWidth: 160,
                renderCell: row => row.aeropay_merchant_id || "",
            },
            {
                id: "aeropay_location_uuid",
                header: "Location UUID",
                minWidth: 200,
                renderCell: row => row.aeropay_location_uuid || "",
            },
        ],
        [isUpdating, showForm],
    );

    const handlerUpdatingOrCreate = array => {
        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...omit(
                        {
                            ...inputs,
                            id: inputs.id,
                            host: inputs.host_setting,
                            api_key: inputs.api_key_setting,
                            api_secret: inputs.api_secret,
                            merchant_id: inputs.merchant_id,
                            credentials: formatCredentials(array),
                            type: "Aeropay",
                        },
                        ["api_key_setting", "host_setting"],
                    ),
                },
            },
        });
    };

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
    };

    const confirmDelete = () => {
        const nextCredentials = _.filter(
            inputs.credentials,
            (credential, index) => index !== rowSelect,
        );
        setCredentials(nextCredentials);
        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...omit(
                        {
                            ...inputs,
                            id: inputs.id,
                            host: inputs.host_setting,
                            api_key: inputs.api_key_setting,
                            credentials: formatCredentials(nextCredentials),
                            type: "Aeropay",
                        },
                        ["api_key_setting", "host_setting"],
                    ),
                },
            },
        });
    };

    return (
        <>
            <Modal
                description="Are You Sure You Want To Remove The Credential ?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setIsDelete(false);
                            }}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                        setIsDelete(false);
                    }
                }}
                open={showDeleteConfirm}
                title="Remove credential"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Aeropay</Heading>
                            <Text tone="secondary" variant="body-sm">
                                Aeropay Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "add-credential",
                                    label: "Add Credential",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: isUpdating || showForm,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowForm(true);
                                    },
                                },
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: loading ? undefined : <SaveIcon />,
                                    loading,
                                    disabled: loading || isUpdating,
                                    onClick: () => createOrUpdate(),
                                },
                            ]}
                            aria-label="Aeropay actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {user.loading || pageLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout>
                        {showForm ? (
                            <AeropayCredentialForm
                                app={inputs}
                                handleCancel={handleCancel}
                                handlerUpdatingOrCreate={
                                    handlerUpdatingOrCreate
                                }
                                isCreate={isCreate}
                                isUpdating={isUpdating}
                                selectCredential={rowSelect}
                                setCredentials={setCredentials}
                                stores={providers}
                            />
                        ) : null}

                        <FormLayout.Section title="Credentials">
                            <Table
                                columns={columns}
                                getRowId={row => String(row._index)}
                                rows={rows}
                            />
                        </FormLayout.Section>
                    </FormLayout>
                )}
            </Panel>
        </>
    );
};

export default AeropayForm;
